import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Upload,
  X,
  XCircle,
} from "lucide-react";
import { useBuckets } from "./shared/useBuckets";
import { SettlementDrawer } from "./shared/SettlementDrawer";
import { settlementsApi } from "./api";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { formatApplicantName } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import {
  SLA_COLOR_MAP,
  formatDateShort,
} from "./shared/labels";
import type { PendingTransferRow } from "./types";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";

export function ToTransferScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [markOpen, setMarkOpen] = useState(false);
  const query = useBuckets();
  const qc = useQueryClient();
  const { push } = useToast();

  const rows = query.data?.buckets.pendientesTransferencia ?? [];
  const slaDays = query.data?.slaDays ?? 7;

  const markFailed = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      settlementsApi.markTransferFailed(id, reason),
    onSuccess: () => {
      push({ kind: "success", message: "Marcada como fallida. Alerta enviada a soporte." });
      qc.invalidateQueries({ queryKey: ["settlements"] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.error || "Error al marcar fallida." }),
  });

  const handleMarkFailed = async (id: number) => {
    const reason = await confirmAction({
      title: `Marcar transferencia #${id} como fallida`,
      promptLabel: "Motivo (obligatorio — Cuenta destino inactiva, RUT incorrecto, etc.)",
      confirmLabel: "Marcar fallida",
      tone: "danger",
    });
    if (reason !== null && (reason as string).trim()) {
      markFailed.mutate({ id, reason: (reason as string).trim() });
    }
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Por transferir</p>
          <h1 className="nx-h1">Pendientes de transferencia</h1>
          <p className="text-sm text-fg-muted mt-1">
            Liquidaciones aceptadas (explícita o tácita) y disputas resueltas
            esperando la transferencia bancaria. SLA configurado:{" "}
            <strong>{slaDays} días</strong>. Color rojo = vencido.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      {query.isLoading ? (
        <LoadingBlock label="Cargando…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable<PendingTransferRow & { id: number }>
          rows={rows.map((r) => ({ ...r, id: r.settlementId }))}
          columns={columns(slaDays)}
          page={1}
          pageSize={rows.length || 1}
          total={rows.length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.settlementId)}
          selectedId={selectedId}
          emptyLabel="No hay liquidaciones pendientes de transferencia. ✅"
        />
      )}

      <SettlementDrawer
        settlementId={selectedId}
        onClose={() => {
          setSelectedId(null);
          setMarkOpen(false);
        }}
        renderActions={(data) => {
          const isPendingTransfer =
            data.settlement.status === "ACEPTADA" || data.settlement.status === "RESUELTA";
          if (!isPendingTransfer) {
            return (
              <span className="text-2xs text-fg-subtle italic">
                Estado: {data.settlement.status}. Sólo se transfiere desde ACEPTADA o RESUELTA.
              </span>
            );
          }
          return (
            <>
              <button
                onClick={() => handleMarkFailed(data.settlement.id)}
                disabled={markFailed.isPending}
                className="nx-btn-danger"
              >
                <XCircle size={14} /> Marcar fallida
              </button>
              <button onClick={() => setMarkOpen(true)} className="nx-btn-primary">
                <Upload size={14} /> Marcar transferida
              </button>
            </>
          );
        }}
      />

      {markOpen && selectedId !== null && (
        <MarkTransferredModal
          settlementId={selectedId}
          amount={
            rows.find((r) => r.settlementId === selectedId)?.amountToTransfer ?? 0
          }
          paymentInfo={
            rows.find((r) => r.settlementId === selectedId)?.paymentInfo
          }
          onClose={() => setMarkOpen(false)}
          onDone={() => {
            setMarkOpen(false);
            setSelectedId(null);
            qc.invalidateQueries({ queryKey: ["settlements"] });
          }}
        />
      )}
    </div>
  );
}

const columns = (slaDays: number): Column<PendingTransferRow & { id: number }>[] => [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.settlementId}</span>,
    width: "min",
  },
  {
    key: "event",
    header: "Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg truncate max-w-xs">{r.eventName}</div>
        <div className="text-2xs text-fg-subtle">{formatDateShort(r.eventDate)}</div>
      </div>
    ),
  },
  {
    key: "productor",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">
          {formatApplicantName({ firstName: r.productorNombre })}
        </div>
        <div className="text-2xs text-fg-subtle break-all">{r.productorEmail ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "amount",
    header: "A transferir",
    cell: (r) => (
      <div className="text-right">
        <div className="text-fg font-semibold tabular-nums">{fmtCLP(r.amountToTransfer)}</div>
        {r.sourceBadge === "post_dispute" && (
          <div className="text-2xs text-warning">post-disputa</div>
        )}
      </div>
    ),
    align: "right",
    width: "min",
  },
  {
    key: "sla",
    header: "Esperando",
    cell: (r) => (
      <div className="flex items-center gap-2">
        <div
          className={cn("w-2 h-2 rounded-full shrink-0", SLA_COLOR_MAP[r.slaColor])}
          title={`SLA ${slaDays}d`}
        />
        <span className="text-xs text-fg tabular-nums">
          {r.daysPending} {r.daysPending === 1 ? "día" : "días"}
        </span>
      </div>
    ),
    width: "min",
  },
  {
    key: "bank",
    header: "Banco",
    cell: (r) =>
      r.paymentInfo.isComplete ? (
        <div>
          <div className="text-xs text-fg">{r.paymentInfo.nombreBanco}</div>
          <div className="text-2xs text-fg-subtle">
            {r.paymentInfo.tipoCuenta} · {r.paymentInfo.numeroCuenta}
          </div>
        </div>
      ) : (
        <span className="text-2xs text-danger flex items-center gap-1">
          <AlertTriangle size={11} /> Datos incompletos
        </span>
      ),
  },
];

// ── Modal de marcar transferida ──────────────────────────────────────
// Exportado: lo reusa BoardScreen (tablero Kanban) para la misma acción.
export function MarkTransferredModal({
  settlementId,
  amount,
  paymentInfo,
  onClose,
  onDone,
}: {
  settlementId: number;
  amount: number;
  paymentInfo?: PendingTransferRow["paymentInfo"];
  onClose: () => void;
  onDone: () => void;
}) {
  const { push } = useToast();

  const [numeroOperacion, setNumeroOperacion] = useState("");
  const [bancoEmisor, setBancoEmisor] = useState("");
  const [fechaTransferencia, setFechaTransferencia] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [notas, setNotas] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const mark = useMutation({
    mutationFn: () =>
      settlementsApi.markTransferred(
        settlementId,
        { numeroOperacion, fechaTransferencia, bancoEmisor, notas },
        files,
      ),
    onSuccess: () => {
      push({ kind: "success", message: "Transferencia registrada. Email enviado al productor." });
      onDone();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.message || "Error al marcar transferida." }),
  });

  const today = new Date().toISOString().slice(0, 10);
  const allowedTypes = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

  const handleFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const arr = Array.from(incoming);
    if (arr.length + files.length > 5) {
      push({ kind: "error", message: "Máximo 5 comprobantes en total." });
      return;
    }
    for (const f of arr) {
      if (f.size > 5 * 1024 * 1024) {
        push({ kind: "error", message: `"${f.name}" supera 5MB.` });
        return;
      }
      if (!allowedTypes.includes(f.type)) {
        push({ kind: "error", message: `"${f.name}": tipo no permitido (sólo JPG/PNG/WebP/PDF).` });
        return;
      }
    }
    setFiles((prev) => [...prev, ...arr]);
  };

  const canSubmit =
    numeroOperacion.trim() &&
    bancoEmisor.trim() &&
    /^\d{4}-\d{2}-\d{2}$/.test(fechaTransferencia) &&
    fechaTransferencia <= today &&
    files.length > 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60] animate-fade-in" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-[61] flex items-center justify-center p-4 pointer-events-none">
        <div className="nx-card pointer-events-auto w-full max-w-lg p-5 animate-fade-in flex flex-col max-h-[92vh]">
          <header className="flex items-center justify-between mb-3">
            <h3 className="nx-h2">✅ Marcar transferencia realizada</h3>
            <button onClick={onClose} className="nx-btn-ghost p-1">
              <X size={14} />
            </button>
          </header>

          <div className="text-2xs text-fg-muted bg-bg-subtle border border-border-subtle rounded p-2 mb-3">
            <div>
              Liquidación <strong>#{settlementId}</strong> · monto{" "}
              <strong className="text-success">{fmtCLP(amount)}</strong>
            </div>
            {paymentInfo?.isComplete ? (
              <div className="mt-1">
                Destino: {paymentInfo.nombreBanco} · {paymentInfo.tipoCuenta} ·{" "}
                {paymentInfo.numeroCuenta} · RUT {paymentInfo.rut}
              </div>
            ) : (
              <div className="mt-1 text-danger">
                ⚠ Datos bancarios del productor incompletos.
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-3">
            <div>
              <label className="nx-eyebrow block mb-1">N° de operación bancaria *</label>
              <input
                type="text"
                value={numeroOperacion}
                onChange={(e) => setNumeroOperacion(e.target.value)}
                placeholder="ej. 12345678"
                className="nx-input"
                autoFocus
              />
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">Banco emisor *</label>
              <input
                type="text"
                value={bancoEmisor}
                onChange={(e) => setBancoEmisor(e.target.value)}
                placeholder="ej. Banco de Chile"
                className="nx-input"
              />
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">
                Fecha de transferencia *{" "}
                <span className="text-fg-subtle normal-case">(no futura)</span>
              </label>
              <input
                type="date"
                value={fechaTransferencia}
                max={today}
                onChange={(e) => setFechaTransferencia(e.target.value)}
                className="nx-input"
              />
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">
                Comprobantes * <span className="text-fg-subtle normal-case">(PDF/JPG/PNG/WebP, máx 5 × 5MB c/u)</span>
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                className="nx-input"
              />
              {files.length > 0 && (
                <div className="mt-2 flex flex-col gap-1">
                  {files.map((f, i) => (
                    <div
                      key={i}
                      className="text-2xs text-fg-muted flex items-center justify-between bg-bg-subtle border border-border-subtle rounded px-2 py-1"
                    >
                      <span className="truncate">
                        {f.name} <span className="text-fg-subtle">({(f.size / 1024).toFixed(0)} KB)</span>
                      </span>
                      <button
                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="text-fg-subtle hover:text-danger"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">Notas (opcional)</label>
              <textarea
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                rows={2}
                placeholder="Observaciones internas…"
                className="nx-input resize-y text-xs"
              />
            </div>
          </div>

          <footer className="flex justify-end gap-2 mt-4 pt-3 border-t border-border">
            <button onClick={onClose} className="nx-btn-secondary" disabled={mark.isPending}>
              Cancelar
            </button>
            <button
              onClick={() => mark.mutate()}
              disabled={!canSubmit || mark.isPending}
              className="nx-btn-primary"
            >
              {mark.isPending ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Confirmar transferencia
            </button>
          </footer>
        </div>
      </div>
    </>
  );
}
